
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Edit, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import DraggableKpiCard from './components/DraggableKpiCard';
import AddKpiCard from './components/AddKpiCard';
import KpiVisibilityManager from './components/KpiVisibilityManager';
import { WaterDashboardProvider, useWaterDashboard } from '@/contexts/water';
import UsageChart from '@/components/UsageChart';

// Import KPI components
import WaterConsumptionKpi from './components/WaterConsumptionKpi';
import WaterFlowRateKpi from './components/WaterFlowRateKpi';
import WaterPressureKpi from './components/WaterPressureKpi';
import WaterIssuesKpi from './components/WaterIssuesKpi';
import WaterQuality from './components/WaterQuality';
import WaterDistributionEfficiency from './components/WaterDistributionEfficiency';
import WaterEquipmentHealth from './components/WaterEquipmentHealth';

const kpiComponents: Record<string, { component: React.ReactNode, size?: 'small' | 'medium' | 'large' }> = {
  'consumption': { component: <WaterConsumptionKpi />, size: 'small' },
  'flowRate': { component: <WaterFlowRateKpi />, size: 'small' },
  'pressure': { component: <WaterPressureKpi />, size: 'small' },
  'issues': { component: <WaterIssuesKpi />, size: 'small' },
  'consumptionChart': { component: <UsageChart className="h-full" utilityType="water" />, size: 'large' },
  'waterQuality': { component: <WaterQuality />, size: 'medium' },
  'distributionEfficiency': { component: <WaterDistributionEfficiency />, size: 'medium' },
  'equipmentHealth': { component: <WaterEquipmentHealth />, size: 'medium' },
  'efficiency': { component: <WaterConsumptionKpi />, size: 'small' },
  'costs': { component: <WaterConsumptionKpi />, size: 'small' },
  'savings': { component: <WaterConsumptionKpi />, size: 'small' },
  'leakage': { component: <WaterIssuesKpi />, size: 'small' },
};

const WaterKpiDashboardContent = () => {
  const { 
    kpiCards, 
    isEditMode, 
    toggleEditMode, 
    drop
  } = useWaterDashboard();
  
  const visibleCards = kpiCards
    .filter(card => card.visible)
    .sort((a, b) => a.order - b.order);
    
  const gridRef = useRef<HTMLDivElement>(null);
  
  const handleGridDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleGridDrop = (e: React.DragEvent) => {
    e.preventDefault();
    drop();
  };

  const scrollLeft = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-end mb-2 flex-shrink-0">
        <div className="flex gap-2">
          {isEditMode && <KpiVisibilityManager />}
          <Button 
            variant={isEditMode ? "default" : "outline"}
            size="sm"
            onClick={toggleEditMode}
          >
            {isEditMode ? (
              <>
                <Save className="mr-1 h-3 w-3" />
                Save Layout
              </>
            ) : (
              <>
                <Edit className="mr-1 h-3 w-3" />
                Customize
              </>
            )}
          </Button>
        </div>
      </div>

      {isEditMode && <AddKpiCard />}

      <div className="relative flex-grow overflow-hidden mt-0">
        <div 
          ref={gridRef}
          className="flex flex-wrap gap-4 p-4 h-full overflow-y-auto overflow-x-auto hide-scrollbar"
          onDragOver={handleGridDragOver}
          onDrop={handleGridDrop}
        >
          {visibleCards.map(card => {
            const cardId = card.id.split('-')[0];
            const cardConfig = kpiComponents[cardId] || { component: (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <p className="text-sm font-medium">{card.title}</p>
                <p className="text-xs">Custom KPI Card</p>
              </div>
            ), size: 'small' };
            
            return (
              <DraggableKpiCard 
                key={card.id} 
                id={card.id} 
                title={card.title}
                size={cardConfig.size}
                className="flex-auto"
              >
                {cardConfig.component}
              </DraggableKpiCard>
            );
          })}
        </div>
        
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8 rounded-full bg-white/80 backdrop-blur-sm shadow-md"
            onClick={scrollLeft}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8 rounded-full bg-white/80 backdrop-blur-sm shadow-md"
            onClick={scrollRight}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const WaterKpiDashboard = () => {
  return (
    <WaterDashboardProvider>
      <WaterKpiDashboardContent />
    </WaterDashboardProvider>
  );
};

export default WaterKpiDashboard;
